import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Download, RefreshCw, ShieldAlert } from 'lucide-react';
import { adminApi } from '../../api/client';
import { buildAuditCsv, auditCsvFilename } from './vatAuditCsv';

const STATUS_BADGE = {
  valid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  invalid: 'bg-rose-100 text-rose-700 border-rose-200',
  missing: 'bg-amber-100 text-amber-700 border-amber-200',
  unsupported_country: 'bg-slate-200 text-slate-700 border-slate-300',
};

const STATUS_LABEL = {
  valid: 'Valid',
  invalid: 'Invalid',
  missing: 'Missing',
  unsupported_country: 'Unsupported country',
};

const FILTER_BUTTONS = [
  ['all', 'Alle'],
  ['critical', 'Kritiek'],
  ['invalid', 'Ongeldig'],
  ['missing', 'Ontbreekt'],
  ['unsupported_country', 'Niet ondersteund'],
  ['valid', 'Geldig'],
];

export default function AdminB2BVatAudit() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('critical');
  const [onlyVatReversed, setOnlyVatReversed] = useState(false);
  const [onlyApproved, setOnlyApproved] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // We always pull the full unfiltered set; client-side filtering keeps
      // the summary counts stable and lets the operator flip filters
      // without re-hitting the API.
      const { data } = await adminApi.vatFormatAudit({});
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Onbekende fout');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    return data.rows.filter(r => {
      if (filter === 'critical' && !r.critical) return false;
      if (filter !== 'all' && filter !== 'critical' && r.status !== filter) return false;
      if (onlyVatReversed && !r.vatReversed) return false;
      if (onlyApproved && !r.isApproved) return false;
      return true;
    });
  }, [data, filter, onlyVatReversed, onlyApproved]);

  const summary = data?.summary;

  const exportCsv = () => {
    // Export the currently filtered view (whatever is on screen). UTF-8
    // BOM so Excel opens it cleanly with accented characters.
    const csv = '\uFEFF' + buildAuditCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = auditCsvFilename();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div data-testid="admin-vat-audit-page" className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link
            to="/admin/b2b"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Terug naar B2B
          </Link>
          <h1 className="font-serif text-3xl font-light text-foreground">
            VAT format-audit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only: controleert <span className="font-mono">b2b_orgs.vatNumber</span> tegen
            de V3 normalisatieregels (BE / NL / FR / LU). Geen mutaties, geen VIES.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="vat-audit-export-csv"
            onClick={exportCsv}
            disabled={loading || !rows.length}
            title={rows.length ? `Exporteer ${rows.length} rijen` : 'Geen rijen om te exporteren'}
            className="inline-flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2 text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            data-testid="vat-audit-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2 text-xs font-semibold hover:bg-muted transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Vernieuwen
          </button>
        </div>
      </div>

      {error && (
        <div
          data-testid="vat-audit-error"
          className="mb-6 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm"
        >
          Fout bij laden: {String(error)}
        </div>
      )}

      {/* Summary tiles */}
      {summary && (
        <div data-testid="vat-audit-summary" className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <SummaryTile testid="tile-total" label="Totaal orgs" value={summary.totalOrgs} />
          <SummaryTile testid="tile-valid"    label="Geldig"      value={summary.valid}    tone="emerald" />
          <SummaryTile testid="tile-invalid"  label="Ongeldig"    value={summary.invalid}  tone="rose" />
          <SummaryTile testid="tile-missing"  label="Ontbreekt"   value={summary.missing}  tone="amber" />
          <SummaryTile testid="tile-unsupported" label="Niet ondersteund" value={summary.unsupported_country} tone="slate" />
          <SummaryTile testid="tile-critical" label="Kritiek"     value={summary.critical} tone="rose" icon={<ShieldAlert className="w-3.5 h-3.5" />} />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTER_BUTTONS.map(([val, label]) => (
          <button
            key={val}
            data-testid={`vat-audit-filter-${val}`}
            onClick={() => setFilter(val)}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
              filter === val
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-card border border-border text-muted-foreground hover:border-primary/30'
            }`}
          >
            {label}
          </button>
        ))}
        <label className="ml-4 inline-flex items-center gap-2 text-xs text-muted-foreground select-none">
          <input
            data-testid="vat-audit-toggle-reversed"
            type="checkbox"
            checked={onlyVatReversed}
            onChange={e => setOnlyVatReversed(e.target.checked)}
          />
          Alleen vatReversed=true
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground select-none">
          <input
            data-testid="vat-audit-toggle-approved"
            type="checkbox"
            checked={onlyApproved}
            onChange={e => setOnlyApproved(e.target.checked)}
          />
          Alleen goedgekeurd
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={`skeleton-${i}`} className="h-12 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Bedrijf</th>
                <th className="px-4 py-3 text-left font-semibold">Land</th>
                <th className="px-4 py-3 text-left font-semibold">Raw VAT</th>
                <th className="px-4 py-3 text-left font-semibold">Genormaliseerd</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-center font-semibold">Goedgekeurd</th>
                <th className="px-4 py-3 text-center font-semibold">vatReversed</th>
                <th className="px-4 py-3 text-left font-semibold">Reden / Kritiek</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map(r => (
                <tr
                  key={r.id}
                  data-testid={`vat-audit-row-${r.id}`}
                  className={r.critical ? 'bg-rose-50/40 hover:bg-rose-50' : 'hover:bg-muted/20'}
                >
                  <td className="px-4 py-3 font-medium text-foreground">
                    {r.critical && (
                      <AlertTriangle
                        data-testid={`vat-audit-critical-${r.id}`}
                        className="inline w-3.5 h-3.5 mr-1.5 text-rose-600"
                      />
                    )}
                    {r.companyName || <span className="text-muted-foreground italic">—</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                    {r.countryCode}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs break-all">
                    {r.rawVatNumber || <span className="italic">leeg</span>}
                  </td>
                  <td className="px-4 py-3 text-emerald-700 font-mono text-xs">
                    {r.normalisedVatNumber || <span className="text-muted-foreground italic">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      data-testid={`vat-audit-status-${r.id}`}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_BADGE[r.status]}`}
                    >
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs">
                    {r.isApproved ? 'ja' : <span className="text-muted-foreground">nee</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-xs">
                    {r.vatReversed ? (
                      <span className="text-rose-700 font-semibold">JA</span>
                    ) : (
                      <span className="text-muted-foreground">nee</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    <div className="space-y-1">
                      {r.reason && <div>{r.reason}</div>}
                      {r.criticalVatReversedWithoutValidVat && (
                        <div className="text-rose-700">⚠ vatReversed zonder geldig VAT</div>
                      )}
                      {r.criticalBeReverseChargeMisconfig && (
                        <div className="text-rose-700">⚠ BE org met vatReversed=true</div>
                      )}
                      {r.criticalApprovedWithInvalidVat && (
                        <div className="text-rose-700">⚠ Goedgekeurd met ongeldig VAT</div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <p data-testid="vat-audit-empty" className="text-center py-12 text-muted-foreground text-sm">
              Geen records voor deze filter.
            </p>
          )}
          <div className="px-4 py-3 text-xs text-muted-foreground border-t border-border">
            {rows.length} resultaten getoond
            {data && rows.length !== data.summary.totalOrgs && (
              <> van {data.summary.totalOrgs} totaal</>
            )}
            .
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ testid, label, value, tone, icon }) {
  const toneClass = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  }[tone] || 'border-border bg-card text-foreground';
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border ${toneClass} px-3 py-3`}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-70 inline-flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="text-2xl font-light mt-0.5">{value ?? '—'}</div>
    </div>
  );
}
